// src/components/PercentageTaxQuarterBarChart.jsx
// Uses getByClient (finds records) + in-memory compute — immune to aggregate ObjectId cast issue.
// Mirrors the same strategy as VATQuarterBarChart.
import React, { useEffect, useState, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import percentageTaxAPI from '../api/percentageTaxAPI';
import formAPI from '../api/formAPI';

const formatPHP = (n) =>
  new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
  }).format(Number(n || 0));

const QUARTER_MONTHS = { 1: [1, 2, 3], 2: [4, 5, 6], 3: [7, 8, 9], 4: [10, 11, 12] };

// Mirrors PercentageTaxFormManagement.periodSummary — computed in memory per quarter
function computeQuarterPayable(pctRecords, form2306Records, quarter) {
  const qRecords = pctRecords.filter((r) => r.quarter === quarter);
  const salesTaxDue = qRecords
    .filter((r) => r.category === 'sales')
    .reduce((s, r) => s + Number(r.taxDue || 0), 0);
  const incomeTaxDue = qRecords
    .filter((r) => r.category === 'income')
    .reduce((s, r) => s + Number(r.taxDue || 0), 0);
  const qMonths = QUARTER_MONTHS[quarter];
  const tax2306 = form2306Records
    .filter((r) => qMonths.includes(r.month))
    .reduce((s, r) => s + Number(r.taxWithheld || 0), 0);
  const netTaxPayable = salesTaxDue - incomeTaxDue - tax2306;
  return { salesTaxDue, incomeTaxDue, tax2306, netTaxPayable, hasRecords: qRecords.length > 0 };
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md p-3 text-sm min-w-[200px]">
      <p className="font-semibold text-gray-900 mb-2">{label}</p>
      {d.hasRecords ? (
        <div className="space-y-1">
          <div className="flex justify-between gap-4">
            <span className="text-gray-500">Sales Tax Due</span>
            <span className="font-medium">{formatPHP(d.salesTaxDue)}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-gray-500">Income Tax Due</span>
            <span className="font-medium">{formatPHP(d.incomeTaxDue)}</span>
          </div>
          {d.tax2306 > 0 && (
            <div className="flex justify-between gap-4">
              <span className="text-gray-500">Form 2306</span>
              <span className="font-medium">{formatPHP(d.tax2306)}</span>
            </div>
          )}
          <hr className="border-gray-200 my-1" />
          <div className="flex justify-between gap-4">
            <span className="font-semibold text-gray-900">
              {d.netTaxPayable >= 0 ? 'Tax Due' : 'Carry-over'}
            </span>
            <span className={`font-bold ${d.netTaxPayable >= 0 ? 'text-red-700' : 'text-green-700'}`}>
              {formatPHP(Math.abs(d.netTaxPayable))}
            </span>
          </div>
        </div>
      ) : (
        <p className="text-gray-400">No records</p>
      )}
    </div>
  );
};

/**
 * PercentageTaxQuarterBarChart
 * Props:
 *   clientId   {string}  — accountingClient _id
 *   fiscalYear {number}  — e.g. 2026
 */
const PercentageTaxQuarterBarChart = ({ clientId, fiscalYear }) => {
  const [pctRecords, setPctRecords]           = useState([]);
  const [form2306Records, setForm2306Records] = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState(null);

  useEffect(() => {
    if (!clientId || !fiscalYear) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [pctRes, frmRes] = await Promise.all([
          percentageTaxAPI.getByClient(clientId, { fiscalYear }),
          formAPI.getForm2306ByClient(clientId, { fiscalYear, limit: 5000 }).catch(() => null),
        ]);

        const pct = Array.isArray(pctRes?.data?.records)
          ? pctRes.data.records
          : Array.isArray(pctRes?.data)
          ? pctRes.data
          : [];

        const frm = Array.isArray(frmRes?.data?.records)
          ? frmRes.data.records
          : Array.isArray(frmRes?.data)
          ? frmRes.data
          : [];

        if (!cancelled) {
          setPctRecords(pct);
          setForm2306Records(frm);
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load Percentage Tax chart data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [clientId, fiscalYear]);

  const chartData = useMemo(() =>
    [1, 2, 3, 4].map((q) => {
      const { salesTaxDue, incomeTaxDue, tax2306, netTaxPayable, hasRecords } =
        computeQuarterPayable(pctRecords, form2306Records, q);
      return {
        period: `Q${q}`,
        amount: hasRecords ? netTaxPayable : 0,
        netTaxPayable, salesTaxDue, incomeTaxDue, tax2306, hasRecords,
        color: !hasRecords ? '#e5e7eb' : netTaxPayable >= 0 ? '#0d9488' : '#2dd4bf',
      };
    }),
  [pctRecords, form2306Records]);

  const allEmpty    = chartData.every((d) => !d.hasRecords);
  const hasNegative = chartData.some((d) => d.hasRecords && d.netTaxPayable < 0);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Percentage Tax Payable — FY {fiscalYear}</h3>
        <p className="text-gray-400 text-sm text-center py-8 animate-pulse">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Percentage Tax Payable — FY {fiscalYear}</h3>
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  if (allEmpty) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Percentage Tax Payable — FY {fiscalYear}</h3>
        <p className="text-gray-400 text-sm text-center py-8">No Percentage Tax records for this year.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Percentage Tax Payable — FY {fiscalYear}</h3>
          <p className="text-xs text-gray-400 mt-0.5">Sales − Income − 2306</p>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-teal-600" /> Tax Due
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-teal-400" /> Carry-over
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-gray-200" /> No Data
          </span>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData} margin={{ top: 20, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0fdf4" />
          <XAxis dataKey="period" tick={{ fill: '#374151', fontWeight: 600 }} />
          <YAxis
            tickFormatter={(v) =>
              `₱${Number(v).toLocaleString('en-PH', { maximumFractionDigits: 0 })}`
            }
            tick={{ fill: '#6b7280', fontSize: 11 }}
            width={90}
          />
          {hasNegative && <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="4 2" />}
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="amount" radius={[6, 6, 0, 0]} maxBarSize={80}>
            {chartData.map((entry, i) => (
              <Cell key={`cell-${i}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PercentageTaxQuarterBarChart;
